import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getConversations, getMessages, sendMessage, getAllUsers } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Send,
  Mail,
  Plus,
  Search,
  User
} from 'lucide-react';

export const MessagesPage = () => {
  const { user, isGuest } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      loadMessages(selectedPartner.partner_id);
    }
  }, [selectedPartner]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await getConversations();
      setConversations(data);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (partnerId) => {
    try {
      const data = await getMessages(partnerId);
      setMessages(data);
      // Update unread count
      setConversations(conversations.map(c => 
        c.partner_id === partnerId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getAllUsers();
      setUsers(data.filter(u => u.user_id !== user?.user_id));
    } catch (error) {
      // User might not be admin, just show empty
      setUsers([]);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedPartner) return;

    setSending(true);
    try {
      const message = await sendMessage({
        recipient_id: selectedPartner.partner_id,
        content: newMessage
      });
      setMessages([...messages, message]);
      setNewMessage('');
      
      // Update conversation list
      const existingConv = conversations.find(c => c.partner_id === selectedPartner.partner_id);
      if (existingConv) {
        setConversations(conversations.map(c =>
          c.partner_id === selectedPartner.partner_id
            ? { ...c, last_message: newMessage, last_message_time: new Date().toISOString() }
            : c
        ));
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const startNewChat = (selectedUser) => {
    setSelectedPartner({
      partner_id: selectedUser.user_id,
      partner_name: selectedUser.name
    });
    setMessages([]);
    setNewChatOpen(false);
    
    // Add to conversations if not exists
    if (!conversations.find(c => c.partner_id === selectedUser.user_id)) {
      setConversations([
        {
          partner_id: selectedUser.user_id,
          partner_name: selectedUser.name,
          last_message: '',
          last_message_time: null,
          unread_count: 0
        },
        ...conversations
      ]);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    return date.toLocaleDateString();
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-8rem)]" data-testid="messages-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Messages</h1>
            <p className="text-muted-foreground font-body mt-1">Direct messaging with team members</p>
          </div>
          <Dialog open={newChatOpen} onOpenChange={setNewChatOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => loadUsers()} data-testid="new-chat-btn">
                <Plus className="h-4 w-4 mr-2" /> New Chat
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start New Conversation</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-users"
                  />
                </div>
                <ScrollArea className="h-[300px]">
                  {filteredUsers.length > 0 ? (
                    <div className="space-y-2">
                      {filteredUsers.map((u) => (
                        <button
                          key={u.user_id}
                          onClick={() => startNewChat(u)}
                          className="w-full flex items-center gap-3 p-3 rounded-sm hover:bg-muted transition-colors text-left"
                          data-testid={`user-${u.user_id}`}
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{u.name}</p>
                            <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No users found</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-3 gap-6 h-[calc(100%-5rem)]">
          {/* Conversations List */}
          <Card className="md:col-span-1 overflow-hidden">
            <CardHeader className="py-4">
              <CardTitle className="text-lg">Conversations</CardTitle>
            </CardHeader>
            <ScrollArea className="h-[calc(100%-4rem)]">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : conversations.length > 0 ? (
                  <div className="space-y-1">
                    {conversations.map((conv) => (
                      <button
                        key={conv.partner_id}
                        onClick={() => setSelectedPartner(conv)}
                        className={`w-full flex items-center gap-3 p-4 transition-colors text-left ${
                          selectedPartner?.partner_id === conv.partner_id
                            ? 'bg-muted'
                            : 'hover:bg-muted/50'
                        }`}
                        data-testid={`conversation-${conv.partner_id}`}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {conv.partner_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium truncate">{conv.partner_name}</p>
                            {conv.unread_count > 0 && (
                              <Badge className="ml-2">{conv.unread_count}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{conv.last_message}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground px-4">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No conversations yet</p>
                    <p className="text-xs mt-1">Start a new chat to connect with your team</p>
                  </div>
                )}
              </CardContent>
            </ScrollArea>
          </Card>

          {/* Chat Area */}
          <Card className="md:col-span-2 flex flex-col overflow-hidden">
            {selectedPartner ? (
              <>
                <CardHeader className="py-4 border-b">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {selectedPartner.partner_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <CardTitle className="text-lg">{selectedPartner.partner_name}</CardTitle>
                  </div>
                </CardHeader>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg, idx) => {
                      const isMine = msg.sender_id === user?.user_id;
                      const showDate = idx === 0 || 
                        formatDate(messages[idx - 1]?.created_at) !== formatDate(msg.created_at);
                      
                      return (
                        <div key={msg.message_id}>
                          {showDate && (
                            <div className="text-center text-xs text-muted-foreground my-4">
                              {formatDate(msg.created_at)}
                            </div>
                          )}
                          <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`message-bubble ${isMine ? 'message-sent' : 'message-received'}`}>
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${isMine ? 'text-white/60' : 'text-muted-foreground'}`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button onClick={handleSendMessage} disabled={sending || !newMessage.trim()} data-testid="send-btn">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="font-semibold mb-1">Select a conversation</h3>
                  <p className="text-sm">Choose from your conversations or start a new chat</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MessagesPage;
